import { useEffect } from "react";
import { MediaQuery } from "@outplayed/responsive";
import { ReactComponent as FilterIcon } from "@ugg/shared/assets/svg/filters-icon.svg";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { useHistory, useParams } from "react-router-dom";
import { getNotFoundImg, getRiotAssetsContext } from "@outplayed/riot-assets";
import { usePageTitleAndDesc } from "@ugg/shared/utils/seo-helpers";
import { RegionS } from "@ugg/shared/utils/region-helpers";
import { REGION_OPTIONS_ALL } from "./leaderboard-filters";

import { LeaderboardError } from "./components/LeaderboardError";
import { LeaderboardLoading } from "./components/LeaderboardLoading";
import { LeaderboardRegionFilter } from "./components/filters/LeaderboardRegionFilter";
import Champion from "@ugg/shared/components/FilterManager/filters/Champion";
import ChampionLeaderboardTableContainer from "./components/ChampionLeaderboardTableContainer";
import { useGetChampionOTPStats, useGetChampionOTPStats2 } from "@ugg/shared/api/requests/champions/otp";
import { getChampionLeaderboard } from "@ugg/shared/routes/app-routes";

const FiltersRow = (props: {
  currentChampId: number;
  onFilterChange: (type: string, option: { value: number | "all" }) => void;
}) => {
  const { currentChampId, onFilterChange } = props;
  const queryParams = useQueryString();
  const { region } = queryParams;

  return (
    <div className="flex items-center my-[24px]">
      <div className="flex items-center">
        <div className="flex items-center mr-[24px] max-sm:mr-[12px]">
          <FilterIcon className="flex-none w-[18px]" />
          <span className="ml-[10px] max-sm:hidden text-[14px] font-bold">Filters</span>
        </div>
        <div className="flex gap-[12px]">
          <LeaderboardRegionFilter
            value={region ? region : RegionS.WORLD}
            options={REGION_OPTIONS_ALL}
            className="region-filter"
          />
          {currentChampId > 0 && (
            <Champion
              value={currentChampId}
              filterKey="championId"
              onFilterChange={onFilterChange}
              disableAllChampsOption={true}
              disableClearButton={true}
              fontSize={12}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export function ChampionLeaderboards() {
  const { getNormalizedChampionName, getChampionName, getChampionIdByName } = getRiotAssetsContext();
  const getPageTitleAndDesc = usePageTitleAndDesc();
  const history = useHistory();
  const { champion } = useParams<{ champion: string }>();
  const queryParams = useQueryString();
  const { region } = queryParams || {};
  const regionFull = region && REGION_OPTIONS_ALL.find((reg) => reg.value === region);
  const { fullLabel } = regionFull || {};

  const currentChampId = getChampionIdByName(champion);
  const championName = getChampionName(currentChampId);

  useEffect(() => {
    if (currentChampId <= 0) {
      history.replace({
        pathname: getChampionLeaderboard("aatrox"),
      });
    }
  }, [currentChampId]);

  const onFilterChange = (type: string, option: { value: number | "all" }) => {
    const search = { ...queryParams };

    if (option.value !== "all") {
      const normalizedChampionName = getNormalizedChampionName(option.value);
      history.push(getChampionLeaderboard(normalizedChampionName, search.region));
    }
  };

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, [JSON.stringify(queryParams)]);

  const {
    data: regionData,
    loading: regionLoading,
    error: regionError,
  } = useGetChampionOTPStats2(currentChampId, region, {
    ssr: false,
  });

  const {
    data: worldData,
    loading: worldLoading,
    error: worldError,
  } = useGetChampionOTPStats(currentChampId, {
    ssr: false,
    skip: currentChampId <= 0,
  });

  const otpWorldData = (worldData?.getChampionOtpStats || []).map((playerStats, i) => ({
    ...playerStats,
    overallRanking: i + 1,
  }));

  const otpRegionData = (regionData?.getChampionOtpStats2 || []).map((playerStats, i) => ({
    ...playerStats,
    overallRanking: i + 1,
  }));

  const data = region && region !== RegionS.WORLD ? otpRegionData : otpWorldData;
  let content = null;

  if (regionLoading || worldLoading) {
    content = <LeaderboardLoading />;
  } else if (data.length === 0 || data.length < 6) {
    content = <LeaderboardError />;
  } else {
    content = (
      <ChampionLeaderboardTableContainer
        page={1}
        data={data}
        championId={currentChampId}
        showRegion={region === "world" || !region}
      />
    );
  }

  return (
    <div className="w-full">
      {getPageTitleAndDesc("champion_leaderboards", {
        champion: { name: championName, key: currentChampId },
        region: fullLabel === "World" ? "the World" : fullLabel ? fullLabel : "the World",
      })}
      <FiltersRow currentChampId={currentChampId} onFilterChange={onFilterChange} />
      {/* <div className="leaderboard-rules">
        <img src={infoIcon} alt="blue information icon" />
        <span>
          To make it onto the champion leaderboard you need to be Grandmaster+
          with at least 15 games played on {championName}
        </span>
      </div> */}
      {content}
    </div>
  );
}
